import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type Profile = {
  id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  website: string | null;
  social_links: Record<string, string>;
  is_artist: boolean;
  created_at: string;
  updated_at: string;
};

export type ArtistProfile = {
  id: string;
  genre: string[];
  approval_status: 'pending' | 'approved' | 'rejected';
  approval_date: string | null;
  approved_by: string | null;
  rejection_reason: string | null;
  monthly_listeners: number;
  verified: boolean;
  created_at: string;
  updated_at: string;
};

export type Track = {
  id: string;
  title: string;
  artist_id: string;
  album_id: string | null;
  duration: number;
  audio_url: string;
  cover_art_url: string | null;
  genre: string[];
  release_date: string;
  is_public: boolean;
  play_count: number;
  created_at: string;
  updated_at: string;
};

export type Album = {
  id: string;
  title: string;
  artist_id: string;
  cover_art_url: string | null;
  release_date: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
};

export type Subscription = {
  id: string;
  user_id: string;
  status: 'active' | 'canceled' | 'expired';
  plan: 'free' | 'premium';
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
  updated_at: string;
};

// Auth helpers
export const signUp = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });
  return { data, error };
};

export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

// Profile helpers
export const getProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();
  return { data, error };
};

export const updateProfile = async (userId: string, updates: Partial<Profile>) => {
  const { data, error } = await supabase
    .from('user_profiles')
    .update(updates)
    .eq('id', userId);
  return { data, error };
};

// Artist profile helpers
export const getArtistProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from('artist_profiles')
    .select('*')
    .eq('id', userId)
    .single();
  return { data, error };
};

export const requestArtistAccess = async (userId: string, genre: string[]) => {
  const { data, error } = await supabase
    .from('artist_profiles')
    .insert([{ id: userId, genre }]);
  return { data, error };
};

// Track helpers
export const uploadTrack = async (
  artistId: string,
  title: string,
  audioFile: File,
  coverArt?: File
) => {
  // Upload audio file
  const audioPath = `tracks/${artistId}/${Date.now()}-${audioFile.name}`;
  const { data: audioData, error: audioError } = await supabase.storage
    .from('audio')
    .upload(audioPath, audioFile);

  if (audioError) {
    return { error: audioError };
  }

  let coverArtUrl = null;
  if (coverArt) {
    // Upload cover art
    const coverArtPath = `covers/${artistId}/${Date.now()}-${coverArt.name}`;
    const { data: coverData, error: coverError } = await supabase.storage
      .from('images')
      .upload(coverArtPath, coverArt);

    if (coverError) {
      // Clean up audio file if cover art upload fails
      await supabase.storage.from('audio').remove([audioPath]);
      return { error: coverError };
    }

    coverArtUrl = supabase.storage.from('images').getPublicUrl(coverArtPath).data.publicUrl;
  }

  // Create track record
  const { data: trackData, error: trackError } = await supabase
    .from('tracks')
    .insert([{
      title,
      artist_id: artistId,
      audio_url: supabase.storage.from('audio').getPublicUrl(audioPath).data.publicUrl,
      cover_art_url: coverArtUrl,
      duration: 0, // You'll need to calculate this client-side
    }]);

  if (trackError) {
    // Clean up uploaded files if track creation fails
    await supabase.storage.from('audio').remove([audioPath]);
    if (coverArtUrl) {
      await supabase.storage.from('images').remove([coverArtUrl]);
    }
    return { error: trackError };
  }

  return { data: trackData };
};

// Subscription helpers
export const getCurrentSubscription = async (userId: string) => {
  const { data, error } = await supabase
    .from('subscriptions')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();
  return { data, error };
};

export const createSubscription = async (userId: string, plan: 'free' | 'premium') => {
  const now = new Date();
  const periodEnd = new Date();
  periodEnd.setMonth(periodEnd.getMonth() + 1);

  const { data, error } = await supabase
    .from('subscriptions')
    .insert([{
      user_id: userId,
      status: 'active',
      plan,
      current_period_start: now.toISOString(),
      current_period_end: periodEnd.toISOString(),
    }]);
  return { data, error };
};